/**
 * One stack for everything that floats.
 *
 * Every panel that opens over the page used to own its own "did you click
 * away" listener, and each one only knew about itself. Open a crew, press
 * somebody's name in it, then click the crew behind: the card stayed and the
 * crew closed, because the card had been told that anything marked
 * data-keeps-card is not "away" and the crew had been told the click landed
 * on its scrim. Two correct answers to two different questions, and the wrong
 * thing on screen.
 *
 * So they share a stack instead. A press works out which layer it landed in
 * and closes everything above that one - press the crew behind the card and
 * the card goes; press the page behind both and both go. Escape takes one off
 * the top, which is the other half of the same idea.
 *
 * What counts as "in" a layer is the element it hands over. A scrim that
 * fills the window is in the stack the same as a small card is, which is what
 * makes pressing the dimmed area around a dialog close what is above it
 * without closing the dialog. Menus, pickers and tooltips are not layers -
 * they belong to whichever layer opened them, and they say so by carrying
 * data-keeps-card.
 */

import { useEffect, useRef } from 'react';

type Layer = {
  id: number;
  /** The element that counts as inside this layer. */
  node: () => Element | null;
  /** Anything else that counts as inside - the control that opened it. */
  keep?: (target: Element) => boolean;
  close: () => void;
};

const stack: Layer[] = [];
let seq = 0;
let wired = false;

/*
 * Who wants to know whether anything is open.
 *
 * A hover card explaining a row is help while you are reading the page; it is
 * in the way the moment you have opened something and are looking at that
 * instead. So the stack says when it is not empty and the hover cards stand
 * down - one rule rather than every panel telling every tooltip about itself.
 */
const focusWatchers = new Set<(open: boolean) => void>();

function announce(): void {
  const open = stack.length > 0;
  for (const fn of focusWatchers) fn(open);
}

/** True while any panel is open over the page. */
export function somethingHasFocus(): boolean { return stack.length > 0; }

/** Called whenever that changes. Returns the way to stop listening. */
export function watchFocus(fn: (open: boolean) => void): () => void {
  focusWatchers.add(fn);
  return () => { focusWatchers.delete(fn); };
}

/** Top down, so a layer never closes one that is still drawn over it. */
function closeAbove(index: number): void {
  for (let i = stack.length - 1; i > index; i--) stack[i]!.close();
}

function ownerOf(target: Element): number {
  for (let i = stack.length - 1; i >= 0; i--) {
    const el = stack[i]!.node();
    if (el && el.contains(target)) return i;
    if (stack[i]!.keep?.(target)) return i;
  }
  return -1;
}

function wire(): void {
  if (wired || typeof document === 'undefined') return;
  wired = true;

  document.addEventListener('pointerdown', (e) => {
    if (!stack.length) return;
    /* Right and middle presses open menus and paste; neither is a request to
       put things away. */
    if (e.button !== 0) return;
    const target = e.target as Element | null;
    if (!target || !target.isConnected) return;

    /*
     * A menu, a picker or a lightbox opened from a layer lives outside it in
     * the page but belongs to it. Those carry data-keeps-card - and a scrim
     * that is itself a layer carries it too, which is why this only bails out
     * for the ones that are not in the stack.
     */
    const keeper = target.closest('[data-keeps-card]');
    if (keeper && !stack.some((l) => l.node() === keeper)) return;

    closeAbove(ownerOf(target));
  }, true);

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !stack.length) return;
    /* One off the top. A layer that wants Escape for something of its own -
       a chain of profiles peeling back - stops the event before this. */
    stack[stack.length - 1]!.close();
  }, false);
}

/**
 * Put this panel on the stack while it is open.
 *
 * `node` is read when a press arrives rather than captured, so a panel that
 * has not rendered its element yet, or that moves between portals, is still
 * asked the right question.
 */
export function useLayer(open: boolean, layer: Omit<Layer, 'id'>): void {
  /*
   * The entry goes on the stack once, when the panel opens, and comes off
   * when it closes. Re-registering on every render would put this layer back
   * on top of anything that opened after it, so the callbacks are read
   * through a box that each render refreshes instead.
   */
  const live = useRef(layer);
  live.current = layer;

  useEffect(() => {
    if (!open) return;
    wire();
    const entry: Layer = {
      id: ++seq,
      node: () => live.current.node(),
      keep: (t) => live.current.keep?.(t) ?? false,
      close: () => live.current.close()
    };
    stack.push(entry);
    announce();
    return () => {
      const at = stack.indexOf(entry);
      if (at >= 0) stack.splice(at, 1);
      announce();
    };
  }, [open]);
}

/** Shut everything, top down. For a route change, where nothing should survive. */
export function closeAllLayers(): void {
  closeAbove(-1);
}

/** How many panels are open. Used by the tests and by nothing in the app. */
export function layerCount(): number { return stack.length; }
